import React, { useState } from 'react';
import { Briefcase, MapPin, Clock, ArrowRight, CheckCircle, Send } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Career = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    experience: '',
    education: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const positions = [
    {
      title: 'İnsan Kaynakları Uzmanı',
      location: 'İstanbul, Beykoz',
      type: 'Tam Zamanlı',
      description: 'İK süreçlerini yönetecek, işe alım ve personel takibini gerçekleştirecek deneyimli uzman arıyoruz.'
    },
    {
      title: 'Bordrolama Uzmanı',
      location: 'İstanbul, Beykoz',
      type: 'Tam Zamanlı',
      description: 'SGK ve bordrolama işlemlerinde deneyimli, detay odaklı uzman arıyoruz.'
    },
    {
      title: 'İSG Uzmanı',
      location: 'İstanbul',
      type: 'Tam Zamanlı',
      description: 'İş sağlığı ve güvenliği alanında sertifikalı, saha deneyimi olan uzman arıyoruz.'
    },
    {
      title: 'Saha Koordinatörü',
      location: 'İstanbul',
      type: 'Tam Zamanlı',
      description: 'Personel koordinasyonu ve saha yönetimi konusunda deneyimli koordinatör arıyoruz.'
    }
  ];

  const benefits = [
    'Rekabetçi maaş ve yan haklar',
    'Kariyer gelişim fırsatları',
    'Dinamik ve profesyonel çalışma ortamı',
    'Sosyal aktiviteler ve etkinlikler',
    'Yemek ve ulaşım desteği',
    'Sağlık sigortası'
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/careers/apply`, formData);
      setSubmitted(true);
      toast.success('Başvurunuz başarıyla gönderildi!');
      setFormData({
        name: '',
        email: '',
        phone: '',
        position: '',
        experience: '',
        education: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error('Başvuru gönderilirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="career-page" className="pt-20">
      {/* Hero Section */}
      <section className="section-spacing bg-gradient-to-br from-[#2A9D8F]/5 to-[#E76F51]/5">
        <div className="section-container">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-block px-4 py-2 bg-[#2A9D8F]/10 text-[#2A9D8F] rounded-full text-sm font-medium mb-6">
              Kariyer
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#264653] mb-6 font-['Outfit']">
              Ekibimize Katılın
            </h1>
            <p className="text-lg text-[#264653]/70 leading-relaxed">
              BDHA Danışmanlık ailesinin bir parçası olun. Profesyonel gelişiminiz için 
              size en iyi ortamı sunuyoruz.
            </p>
          </div>
        </div>
      </section>

      {/* Why Join Us */}
      <section className="section-spacing" data-testid="benefits-section">
        <div className="section-container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#2A9D8F] font-medium text-sm uppercase tracking-wider">
                Neden BDHA?
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#264653] mt-4 mb-8 font-['Outfit']">
                Size Sunduklarımız
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="text-[#2A9D8F] shrink-0" size={20} />
                    <span className="text-[#264653]/80">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <img
                src="https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg"
                alt="Ekibimiz"
                className="rounded-3xl shadow-xl w-full object-cover h-[400px]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="section-spacing bg-[#264653]/5" data-testid="positions-section">
        <div className="section-container">
          <div className="text-center mb-16">
            <span className="text-[#2A9D8F] font-medium text-sm uppercase tracking-wider">
              Açık Pozisyonlar
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#264653] mt-4 font-['Outfit']">
              Kariyer Fırsatları
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {positions.map((position, index) => (
              <div
                key={index}
                className="p-6 rounded-2xl bg-white border border-[#264653]/5 hover:shadow-lg transition-shadow"
                data-testid={`position-${index}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-[#2A9D8F]/10 rounded-xl flex items-center justify-center">
                    <Briefcase className="text-[#2A9D8F]" size={24} />
                  </div>
                  <span className="px-3 py-1 bg-[#E76F51]/10 text-[#E76F51] text-xs font-medium rounded-full">
                    {position.type}
                  </span>
                </div>
                <h3 className="text-xl font-semibold text-[#264653] mb-2 font-['Outfit']">
                  {position.title}
                </h3>
                <div className="flex items-center gap-4 text-sm text-[#264653]/60 mb-4">
                  <span className="flex items-center gap-1">
                    <MapPin size={14} /> {position.location}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock size={14} /> {position.type}
                  </span>
                </div>
                <p className="text-[#264653]/70 text-sm mb-4">{position.description}</p>
                <a
                  href="#basvuru-formu"
                  className="inline-flex items-center gap-2 text-[#2A9D8F] font-medium text-sm hover:gap-3 transition-all"
                >
                  Başvur <ArrowRight size={16} />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="basvuru-formu" className="section-spacing" data-testid="application-form-section">
        <div className="section-container">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-[#2A9D8F] font-medium text-sm uppercase tracking-wider">
                Başvuru Formu
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#264653] mt-4 font-['Outfit']">
                Kariyer Başvurusu
              </h2>
              <p className="text-[#264653]/70 mt-4">
                Aşağıdaki formu doldurarak kariyer başvurunuzu yapabilirsiniz.
              </p>
            </div>

            {submitted ? (
              <div className="text-center p-12 rounded-3xl bg-[#2A9D8F]/5 border border-[#2A9D8F]/10">
                <div className="w-16 h-16 mx-auto bg-[#2A9D8F]/10 rounded-full flex items-center justify-center mb-6">
                  <CheckCircle className="text-[#2A9D8F]" size={32} />
                </div>
                <h3 className="text-2xl font-bold text-[#264653] mb-4 font-['Outfit']">
                  Başvurunuz Alındı!
                </h3>
                <p className="text-[#264653]/70 mb-6">
                  Başvurunuzu inceleyeceğiz ve sizinle en kısa sürede iletişime geçeceğiz.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="btn-primary"
                >
                  Yeni Başvuru Yap
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="career-form">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-[#264653] mb-2">
                      Ad Soyad *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="form-input"
                      placeholder="Adınız Soyadınız"
                      data-testid="career-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#264653] mb-2">
                      E-posta *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="form-input"
                      placeholder="ornek@email.com"
                      data-testid="career-email-input"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-[#264653] mb-2">
                      Telefon *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="form-input"
                      placeholder="0 5XX XXX XX XX"
                      data-testid="career-phone-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#264653] mb-2">
                      Başvurulan Pozisyon *
                    </label>
                    <select
                      name="position"
                      value={formData.position}
                      onChange={handleChange}
                      required
                      className="form-input"
                      data-testid="career-position-select"
                    >
                      <option value="">Pozisyon Seçin</option>
                      {positions.map((pos, index) => (
                        <option key={index} value={pos.title}>{pos.title}</option>
                      ))}
                      <option value="Diğer">Diğer</option>
                    </select>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-[#264653] mb-2">
                      Deneyim *
                    </label>
                    <select
                      name="experience"
                      value={formData.experience}
                      onChange={handleChange}
                      required
                      className="form-input"
                      data-testid="career-experience-select"
                    >
                      <option value="">Deneyim Süreniz</option>
                      <option value="0-1 Yıl">0-1 Yıl</option>
                      <option value="1-3 Yıl">1-3 Yıl</option>
                      <option value="3-5 Yıl">3-5 Yıl</option>
                      <option value="5+ Yıl">5+ Yıl</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#264653] mb-2">
                      Eğitim Durumu *
                    </label>
                    <select
                      name="education"
                      value={formData.education}
                      onChange={handleChange}
                      required
                      className="form-input"
                      data-testid="career-education-select"
                    >
                      <option value="">Eğitim Durumunuz</option>
                      <option value="Lise">Lise</option>
                      <option value="Ön Lisans">Ön Lisans</option>
                      <option value="Lisans">Lisans</option>
                      <option value="Yüksek Lisans">Yüksek Lisans</option>
                      <option value="Doktora">Doktora</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#264653] mb-2">
                    Ek Bilgiler
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    className="form-textarea"
                    placeholder="Kendinizi kısaca tanıtın..."
                    data-testid="career-message-textarea"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  data-testid="career-submit-btn"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                  ) : (
                    <>
                      Başvuru Gönder <Send size={18} />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Career;
